import React from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { SVG_COMPONENTS as ADMIN_SVG_COMPONENTS } from '@/assets/admin';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { CHAT_MOBILE_COLORS } from '@/constants/residents/mobile/chatColors';
import { RESIDENTS_MOBILE_MESSAGE_MAX_LENGTH } from '@/constants/residents/mobile/message';

type MessageInputBarProps = {
  messageText: string;
  setMessageText: (text: string) => void;
  onSend: () => void;
  disabled?: boolean;
  placeholder?: string;
  onPressPaperclip?: () => void;
  onFocusScrollToBottom?: () => void;
  maxLength?: number;
  isSending?: boolean;
};

export const MessageInputBar: React.FC<MessageInputBarProps> = ({
  messageText,
  setMessageText,
  onSend,
  disabled,
  placeholder = 'Your message...',
  onPressPaperclip,
  onFocusScrollToBottom,
  maxLength = RESIDENTS_MOBILE_MESSAGE_MAX_LENGTH,
  isSending = false,
}) => {
  const remaining = maxLength - messageText.length;
  const overLimit = messageText.length > maxLength;
  const atOrOverLimit = remaining <= 0;
  const finalDisabled = !!disabled || overLimit || isSending;

  return (
    <View style={stylesRef.messageInputFooter}>
      <View style={stylesRef.inputRowContainer}>
        <View style={stylesRef.inputRow}>
          {/* Attach button */}
          <View style={stylesRef.attachButtonWrapper}>
            <TouchableOpacity style={stylesRef.attachButton} onPress={onPressPaperclip}>
              <ADMIN_SVG_COMPONENTS.PAPERCLIP width={20} height={20} color={CHAT_MOBILE_COLORS.textMuted} />
            </TouchableOpacity>
          </View>

          {/* Text input */}
          <TextInput
            style={stylesRef.messageInput}
            value={messageText}
            onChangeText={setMessageText}
            placeholder={placeholder}
            placeholderTextColor={CHAT_MOBILE_COLORS.textMuted}
            multiline
            textAlignVertical="top"
            onFocus={onFocusScrollToBottom}
          />

          {/* Send button or counter */}
          {atOrOverLimit ? (
            <View style={stylesRef.counterContainer}>
              <Text style={stylesRef.counterText}>{remaining}</Text>
            </View>
          ) : (
            <TouchableOpacity
              onPress={onSend}
              disabled={finalDisabled}
              style={stylesRef.sendButton}
              accessibilityLabel="Send Message"
            >
              {isSending ? (
                <ActivityIndicator size="small" color={CHAT_MOBILE_COLORS.textPrimary} />
              ) : !finalDisabled ? (
                <RESIDENTS_SVG_COMPONENTS.CHAT_SEND_ACTIVE_BUTTON width={34} height={34} />
              ) : (
                <RESIDENTS_SVG_COMPONENTS.CHAT_SEND_INACTIVE_BUTTON width={34} height={34} />
              )}
            </TouchableOpacity>
          )}
        </View>
      </View>
    </View>
  );
};

const stylesRef = StyleSheet.create({
  messageInputFooter: {
    backgroundColor: CHAT_MOBILE_COLORS.cardBackground,
    borderTopWidth: 1,
    borderTopColor: CHAT_MOBILE_COLORS.divider,
  },
  inputRowContainer: {
    paddingHorizontal: 24,
    paddingVertical: 20,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  attachButtonWrapper: {
    paddingTop: 5,
  },
  attachButton: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  messageInput: {
    flex: 1,
    minHeight: 21,
    maxHeight: 100,
    fontSize: 14,
    fontWeight: '400',
    color: CHAT_MOBILE_COLORS.textPrimary,
    lineHeight: 21,
    paddingVertical: 6,
  },
  sendButton: {
    width: 34,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
  },
  counterContainer: {
    width: 34,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
  },
  counterText: {
    color: CHAT_MOBILE_COLORS.accentPink,
    fontSize: 13,
    fontWeight: '600',
  },
});

export default MessageInputBar;
